// src/pages/CoursePage.jsx
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { lmsAPI } from '../services/api';
import ProgressRing from '../components/ui/ProgressRing';

const TYPE_ICONS = {
  text: '📄', video: '▶', file: '📎', live_session: '🎥',
  quiz: '🧠', assignment: '📝', practical_evidence: '📸', portfolio: '🗂',
};

function LessonItem({ lesson, current, progress, onClick }) {
  const lp     = progress?.find(p => p.lesson_id === lesson.id) || progress?.find(p => p.id === lesson.id);
  const status = lp?.status || 'not_started';
  const done   = status === 'completed';
  const active = current?.id === lesson.id;

  return (
    <button onClick={() => onClick(lesson)}
      style={{
        width:'100%', textAlign:'left', padding:'10px 14px', border:'none', cursor:'pointer',
        borderRadius:'var(--r-md)', marginBottom:3,
        background: active ? 'rgba(232,164,58,0.1)' : 'transparent',
        borderLeft: active ? '2px solid var(--gold)' : '2px solid transparent',
        transition:'all 0.15s',
        display:'flex', alignItems:'center', gap:10,
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-3)'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>

      {/* Status indicator */}
      <div style={{
        width:20, height:20, borderRadius:'50%', flexShrink:0,
        display:'flex', alignItems:'center', justifyContent:'center', fontSize:10,
        background: done ? 'rgba(62,207,142,0.15)' : active ? 'rgba(232,164,58,0.15)' : 'var(--bg-3)',
        border: `1px solid ${done ? 'rgba(62,207,142,0.4)' : active ? 'rgba(232,164,58,0.4)' : 'var(--border)'}`,
        color: done ? 'var(--teal)' : active ? 'var(--gold)' : 'var(--t-muted)',
      }}>
        {done ? '✓' : TYPE_ICONS[lesson.lesson_type] || '·'}
      </div>

      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:'0.82rem', fontWeight: active ? 500 : 400, color: active ? 'var(--gold)' : done ? 'var(--t-secondary)' : 'var(--t-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {lesson.title}
        </div>
        {lesson.estimated_minutes && (
          <div style={{ fontSize:'0.7rem', color:'var(--t-muted)' }}>{lesson.estimated_minutes}m</div>
        )}
      </div>

      {!lesson.is_required && <span style={{ fontSize:'0.6rem', color:'var(--t-muted)', flexShrink:0 }}>opt</span>}
    </button>
  );
}

export default function CoursePage() {
  const { courseId }    = useParams();
  const navigate        = useNavigate();
  const [course, setCourse]   = useState(null);
  const [progress, setProgress] = useState([]);
  const [current, setCurrent] = useState(null);
  const [enrollmentId, setEnrollmentId] = useState(null);
  const [overallPct, setOverallPct] = useState(0);
  const [loading, setLoading]   = useState(true);
  const [sidebarOpen, setSidebar] = useState(true);

  const load = useCallback(async () => {
    try {
      const [cRes, pRes] = await Promise.all([
        lmsAPI.getCourse(courseId),
        lmsAPI.myCourseProgress(courseId),
      ]);
      const c = cRes.data.data;
      const p = pRes.data.data;

      setCourse(c);
      setProgress(p.lessons || []);
      setEnrollmentId(p.enrollment_id);
      setOverallPct(p.lms_progress_pct || 0);

      // Auto-open first incomplete lesson
      const allLessons = (c.modules || []).flatMap(m => m.lessons || []);
      if (allLessons.length && !current) {
        const firstIncomplete = allLessons.find(l => {
          const lp = (p.lessons||[]).find(x => x.id === l.id);
          return !lp || lp.status !== 'completed';
        }) || allLessons[0];
        setCurrent(firstIncomplete);
      }
    } catch (e) {
      if (e.response?.status === 404) navigate('/dashboard');
    } finally { setLoading(false); }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ paddingTop:'var(--nav-h)', minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--t-muted)' }}>
      Loading course…
    </div>
  );

  if (!course) return null;

  const allLessons   = (course.modules || []).flatMap(m => m.lessons || []);
  const currentIndex = allLessons.findIndex(l => l.id === current?.id);
  const prevLesson   = currentIndex > 0 ? allLessons[currentIndex - 1] : null;
  const nextLesson   = currentIndex < allLessons.length - 1 ? allLessons[currentIndex + 1] : null;

  return (
    <div style={{ paddingTop:'var(--nav-h)', minHeight:'100vh', display:'flex' }}>

      {/* ── SIDEBAR ── */}
      <aside style={{
        width: sidebarOpen ? 'var(--sidebar-w)' : 0,
        flexShrink: 0, overflow: 'hidden',
        transition: 'width 0.3s ease',
        borderRight: '1px solid var(--border)',
        background: 'var(--bg-1)',
        position: 'fixed', top: 'var(--nav-h)', bottom: 0, left: 0,
        display: 'flex', flexDirection: 'column',
        zIndex: 50,
      }}>
        <div style={{ flex:1, overflowY:'auto', padding:'16px 12px' }}>

          {/* Course title + progress */}
          <div style={{ padding:'12px 8px 16px', borderBottom:'1px solid var(--border)', marginBottom:12 }}>
            <div style={{ fontSize:'0.72rem', color:'var(--t-muted)', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:4 }}>Course</div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:'0.95rem', fontWeight:600, lineHeight:1.3, marginBottom:12 }}>{course.title}</div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <ProgressRing pct={overallPct} size={36} stroke={3}/>
              <div>
                <div style={{ fontSize:'0.82rem', fontWeight:600, color:'var(--gold)' }}>{overallPct}%</div>
                <div style={{ fontSize:'0.7rem', color:'var(--t-muted)' }}>Complete</div>
              </div>
            </div>
          </div>

          {/* Modules + lessons */}
          {(course.modules || []).map((mod, mi) => (
            <div key={mod.id} style={{ marginBottom:16 }}>
              <div style={{ fontSize:'0.68rem', fontWeight:600, color:'var(--t-muted)', letterSpacing:'0.1em', textTransform:'uppercase', padding:'4px 8px 8px' }}>
                Module {mi + 1} · {mod.title}
              </div>
              {(mod.lessons || []).map(les => (
                <LessonItem key={les.id} lesson={les} current={current} progress={progress} onClick={setCurrent}/>
              ))}
            </div>
          ))}
        </div>

        {/* Back to dashboard */}
        <div style={{ padding:'12px', borderTop:'1px solid var(--border)' }}>
          <Link to="/dashboard" style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.8rem', color:'var(--t-muted)', padding:'8px 10px', borderRadius:'var(--r-md)', transition:'all 0.15s' }}
            onMouseEnter={e=>{e.currentTarget.style.color='var(--t-primary)';e.currentTarget.style.background='var(--bg-3)';}}
            onMouseLeave={e=>{e.currentTarget.style.color='var(--t-muted)';e.currentTarget.style.background='transparent';}}>
            ← All Courses
          </Link>
        </div>
      </aside>

      {/* ── MAIN CONTENT ── */}
      <main style={{ flex:1, marginLeft: sidebarOpen ? 'var(--sidebar-w)' : 0, transition:'margin-left 0.3s ease', minWidth:0 }}>

        {/* Top bar */}
        <div style={{ position:'sticky', top:'var(--nav-h)', zIndex:40, background:'rgba(12,13,15,0.95)', backdropFilter:'blur(8px)', borderBottom:'1px solid var(--border)', padding:'10px 24px', display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={() => setSidebar(s => !s)} style={{ width:32, height:32, borderRadius:8, background:'var(--bg-3)', border:'1px solid var(--border)', color:'var(--t-secondary)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0 }}>
            {sidebarOpen ? '‹' : '›'}
          </button>

          {current && (
            <>
              <div style={{ fontSize:'0.72rem', color:'var(--t-muted)' }}>
                {(course.modules||[]).find(m=>m.lessons?.find(l=>l.id===current.id))?.title}
              </div>
              <div style={{ color:'var(--border)', fontSize:'0.8rem' }}>›</div>
              <div style={{ fontSize:'0.88rem', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>{current.title}</div>
            </>
          )}

          <div style={{ marginLeft:'auto', display:'flex', gap:8, flexShrink:0 }}>
            {prevLesson && (
              <button onClick={() => setCurrent(prevLesson)} className="btn btn-ghost" style={{ fontSize:'0.78rem', padding:'6px 12px' }}>← Prev</button>
            )}
            {nextLesson && (
              <button onClick={() => setCurrent(nextLesson)} className="btn btn-outline" style={{ fontSize:'0.78rem', padding:'6px 12px' }}>Next →</button>
            )}
          </div>
        </div>

        {/* Lesson content */}
        {current ? (
          <LessonView lesson={current} enrollmentId={enrollmentId} progress={progress} onComplete={load}/>
        ) : (
          <div style={{ padding:60, textAlign:'center', color:'var(--t-muted)' }}>
            Select a lesson from the sidebar to begin.
          </div>
        )}
      </main>
    </div>
  );
}

// ── Lesson content renderer ───────────────────────────────
import LessonView from '../components/lesson/LessonView';
